<script setup lang="ts">
import { Lock } from "@iconoir/vue";
import { reactive, onMounted, ref } from "vue";
import { useResponseError } from "~/composables/error";
import ChangePasswordModal from "./ChangePasswordModal.vue";

defineOptions({ name: "Profile" });

const { $api } = useNuxtApp();
const { t } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const { set: setTitle } = usePageTitle();
const { formErrors, errorMsg, setErrors, clearErrors } = useResponseError();

const route = useRoute();

const form = reactive({
  fullName: "",
  email: "",
  desc: "",
});

const loadLoading = ref(true);
const saveLoading = ref(false);

const passwordModalRef = ref<InstanceType<typeof ChangePasswordModal> | null>(null);

async function load() {
  loadLoading.value = true;
  try {
    const data: any = await $api("/profile");
    form.fullName = data?.fullName || "";
    form.email = data?.email || "";
    form.desc = data?.desc || "";
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("profile.loadError") });
  } finally {
    loadLoading.value = false;
  }
}

async function submit() {
  clearErrors();
  saveLoading.value = true;
  try {
    await $api("/profile", {
      method: "POST",
      body: {
        fullName: form.fullName,
        email: form.email,
        desc: form.desc,
      },
    });
    if (authStore.user) {
      authStore.updateUser({
        ...authStore.user,
        fullName: form.fullName,
        desc: form.desc,
      });
    }
    toast.success({ title: t("profile.saveSuccess") });
  } catch (e: any) {
    setErrors(e);
    if (errorMsg.value && Object.keys(formErrors.value).length === 0) {
      toast.error({ title: errorMsg.value });
    }
  } finally {
    saveLoading.value = false;
  }
}

onMounted(async () => {
  setTitle(t("profile.title"));
  await load();
  if (route.query.action === "changePassword") {
    passwordModalRef.value?.open();
  }
});
</script>

<template>
  <div class="profile flex flex-col gap-medium">
    <div class="flex flex-col gap-2xsmall">
      <h1 class="h3-primary">{{ t("profile.title") }}</h1>
      <p class="text-body-tertiary content-neutral-medium">
        {{ t("profile.description") }}
      </p>
    </div>

    <div v-if="loadLoading" class="profile__loading">
      <ProgressCircle size="small" />
    </div>

    <div v-else class="profile__panel">
      <form
        class="flex flex-col gap-medium"
        autocomplete="off"
        data-testid="profile-form"
        @submit.prevent="submit"
      >
        <FormField
          v-model="form.fullName"
          :label="t('field.fullName')"
          :error="formErrors.fullName"
        />

        <FormField
          v-model="form.email"
          type="email"
          :label="t('field.email')"
          :disabled="true"
          :help-text="t('profile.emailLockedHelp')"
          :error="formErrors.email"
        />

        <FormTextarea
          v-model="form.desc"
          :label="t('field.description')"
          :placeholder="t('profile.descPlaceholder')"
          :error="formErrors.desc"
        />

        <div class="flex items-center justify-between gap-small flex-wrap">
          <Button
            variant="tertiary"
            size="small"
            type="button"
            @click="passwordModalRef?.open()"
          >
            <Lock class="icon" />
            {{ t("profile.changePassword.trigger") }}
          </Button>
          <Button
            variant="primary"
            size="small"
            :disabled="saveLoading"
            data-testid="profile-save"
            @click="submit"
          >
            <ProgressCircle v-if="saveLoading" size="small" />
            {{ t("action.save") }}
          </Button>
        </div>
      </form>
    </div>

    <ChangePasswordModal ref="passwordModalRef" />
  </div>
</template>

<style scoped lang="scss">
.profile {
  padding: token("padding-xsmall") 0 token("padding-medium");
  max-width: 720px;

  @include up("md") {
    padding: token("padding-xsmall") 0 token("padding-large");
  }
}

.profile__loading {
  display: flex;
  justify-content: center;
  padding: token("padding-2xlarge");
}

.profile__panel {
  padding: token("padding-xlarge");
  border-radius: token("corner-radius-small");
  background: token("color-bg-surface");

  @include down("md") {
    padding: token("padding-large");
  }

  @include down("sm") {
    padding: token("padding-medium");
  }
}
</style>
